'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { AuthInfiniteQueryOptions } from '@/api';
import type { PeriodOption } from '../constants';
import type { OrderStatusValue } from '../types';
import { OrderCard } from './OrderCard';
interface OrderListProps {
  orderStatus?: OrderStatusValue;
  period: PeriodOption;
}
const PAGE_SIZE = 10;
export const OrderList = t0 => {
  const $ = _c(27);
  const {
    orderStatus,
    period
  } = t0;
  const endDate = dayjs().format("YYYY-MM-DD");
  const dateQuery = period.value === "all" ? {} : {
    startDate: dayjs().subtract(period.amount, period.unit).format("YYYY-MM-DD"),
    endDate
  };
  const bottomRef = useRef(null);
  const t1 = AuthInfiniteQueryOptions["get:/wello/v2/auth/local-market-orders/unified"]({
    query: {
      orderStatus: orderStatus || undefined,
      ...dateQuery,
      size: PAGE_SIZE
    }
  });
  let t2;
  if ($[0] !== t1) {
    t2 = {
      ...t1,
      initialPageParam: 1,
      getNextPageParam: _temp
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t2);
  let t3;
  let t4;
  if ($[2] !== fetchNextPage || $[3] !== hasNextPage || $[4] !== isFetchingNextPage) {
    t3 = () => {
      const bottomElement = bottomRef.current;
      if (!bottomElement || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(t5 => {
        const [entry] = t5;
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1
      });
      observer.observe(bottomElement);
      return () => observer.disconnect();
    };
    t4 = [hasNextPage, isFetchingNextPage, fetchNextPage];
    $[2] = fetchNextPage;
    $[3] = hasNextPage;
    $[4] = isFetchingNextPage;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  let t7;
  if ($[7] !== data?.pages || $[8] !== isError || $[9] !== isLoading) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const orders = data?.pages.flatMap(_temp2) ?? [];
      if (isLoading) {
        let t8;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <div className="flex min-h-200 items-center justify-center"><p className="font-500 text-Gray-600 typo-body4">불러오는 중...</p></div>;
          $[13] = t8;
        } else {
          t8 = $[13];
        }
        t7 = t8;
        break bb0;
      }
      if (isError) {
        let t8;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <OrderListErrorFallback />;
          $[14] = t8;
        } else {
          t8 = $[14];
        }
        t7 = t8;
        break bb0;
      }
      if (orders.length === 0) {
        let t8;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <div className="flex min-h-200 flex-col items-center justify-center gap-8"><p className="font-500 text-Gray-600 typo-body4">주문 내역이 없습니다.</p></div>;
          $[15] = t8;
        } else {
          t8 = $[15];
        }
        t7 = t8;
        break bb0;
      }
      t5 = "flex flex-col";
      t6 = orders.map(_temp3);
    }
    $[7] = data?.pages;
    $[8] = isError;
    $[9] = isLoading;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t6) {
    t8 = <ul className={t5}>{t6}</ul>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isFetchingNextPage) {
    t9 = isFetchingNextPage && <div className="flex items-center justify-center py-20"><p className="font-500 text-Gray-600 typo-body4">불러오는 중...</p></div>;
    $[19] = isFetchingNextPage;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== hasNextPage) {
    t10 = hasNextPage && <div ref={bottomRef} className="h-1" />;
    $[21] = hasNextPage;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
    t11 = <>{t8}{t9}{t10}</>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
export const OrderListErrorFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="size-full flex-col gap-12 rounded-small flex-center"><p className="font-500 text-Gray-700 typo-body4">주문 내역을 불러올 수 없습니다.</p><Button dimensions="H40" theme="sub-line" onClick={_temp4}>다시 시도하기</Button></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(lastPage, _allPages, lastPageParam) {
  const totalCount = lastPage?.context?.totalCount ?? 0;
  const currentPage = lastPageParam as number;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  return currentPage < totalPages ? currentPage + 1 : undefined;
}
function _temp2(page) {
  return page?.context?.contents ?? [];
}
function _temp3(order) {
  return <OrderCard key={order.orderId} order={order} />;
}
function _temp4() {
  window.location.reload();
}